import React, { useState, useEffect, useRef } from 'react';
import { FiSend, FiMessageCircle, FiArrowLeft } from 'react-icons/fi';
import { messagesAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'timeago.js';

export default function Messages() {
  const { user: me }            = useAuth();
  const [convos, setConvos]     = useState([]);
  const [active, setActive]     = useState(null);
  const [thread, setThread]     = useState([]);
  const [msg, setMsg]           = useState('');
  const [loading, setLoading]   = useState(true);
  const [sending, setSending]   = useState(false);
  const bottomRef               = useRef();

  useEffect(() => {
    messagesAPI.getConversations()
      .then(r => setConvos(r.data.conversations || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!active) return;
    const otherId = +active.sender_id === +me?.id ? active.receiver_id : active.sender_id;
    messagesAPI.getThread(otherId)
      .then(r => { setThread(r.data.messages || []); setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80); })
      .catch(() => {});
  }, [active, me]);

  const sendMsg = async e => {
    e.preventDefault();
    if (!msg.trim() || !active) return;
    const otherId = +active.sender_id === +me?.id ? active.receiver_id : active.sender_id;
    const text = msg.trim();
    setMsg('');
    setSending(true);
    try {
      await messagesAPI.sendMessage(otherId, text);
      setThread(p => [...p, { id: Date.now(), sender_id: me.id, content: text, created_at: new Date().toISOString(), username: me.username, avatar: me.avatar }]);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
    } catch {}
    finally { setSending(false); }
  };

  const otherUser = active
    ? { id: +active.sender_id === +me?.id ? active.receiver_id : active.sender_id, username: active.username, avatar: active.avatar }
    : null;

  const avatarPh = (name, size) => (
    <div style={{ width: size, height: size, borderRadius: '50%', background: 'linear-gradient(135deg,#E91E8C,#7B2FF7)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: size * 0.36, flexShrink: 0 }}>
      {name?.[0]?.toUpperCase()}
    </div>
  );

  return (
    <div className="messages-layout">
      {/* Conversations panel */}
      <div className={"convos-panel" + (active ? ' hidden-mobile' : '')}>
        <div style={{ padding: 'clamp(12px,2.5vw,18px) clamp(14px,3vw,22px)', borderBottom: '1px solid var(--border)' }}>
          <h2>Messages</h2>
        </div>

        {loading ? (
          <div style={{ padding: 'var(--sp-4)' }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ display: 'flex', gap: 12, padding: '12px 0', alignItems: 'center' }}>
                <div className="skeleton" style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div className="skeleton" style={{ width: '55%', height: 10, marginBottom: 8 }} />
                  <div className="skeleton" style={{ width: '80%', height: 8 }} />
                </div>
              </div>
            ))}
          </div>
        ) : convos.length === 0 ? (
          <div style={{ padding: 'var(--sp-6) var(--sp-4)', textAlign: 'center', color: 'var(--text-3)' }}>
            <FiMessageCircle size={44} style={{ margin: '0 auto var(--sp-3)', display: 'block' }} />
            <p style={{ fontSize: 'var(--fs-sm)' }}>No conversations yet.</p>
          </div>
        ) : convos.map(c => (
          <div key={c.id} className={"convo-item" + (active?.id === c.id ? ' active' : '')} onClick={() => setActive(c)}>
            {c.avatar ? <img src={c.avatar} className="avatar" style={{ width: 'clamp(38px,7vw,46px)', height: 'clamp(38px,7vw,46px)' }} alt="" /> : avatarPh(c.full_name || c.username, 42)}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p className="font-bold truncate" style={{ fontSize: 'var(--fs-sm)' }}>{c.username}</p>
              <p className="truncate text-3" style={{ fontSize: 'var(--fs-xs)' }}>{c.content}</p>
            </div>
            {+c.unread > 0 && <span className="unread-badge">{c.unread}</span>}
          </div>
        ))}
      </div>

      {/* Thread panel */}
      <div className={"thread-panel" + (!active ? ' hidden-mobile' : '')}>
        {!active ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-3)', gap: 'var(--sp-3)' }}>
            <FiMessageCircle size={52} />
            <p className="font-bold" style={{ fontSize: 'var(--fs-lg)' }}>Select a conversation</p>
          </div>
        ) : (
          <>
            <div className="thread-header">
              <button className="btn btn-icon btn-ghost show-mobile" onClick={() => setActive(null)}>
                <FiArrowLeft size={20} />
              </button>
              {otherUser?.avatar ? <img src={otherUser.avatar} className="avatar" style={{ width: 36, height: 36 }} alt="" /> : avatarPh(otherUser?.username, 36)}
              <span className="font-bold">@{otherUser?.username}</span>
            </div>

            <div className="thread-msgs">
              {thread.map(m => {
                const isMine = +m.sender_id === +me?.id;
                return (
                  <div key={m.id} className={"msg-row" + (isMine ? ' mine' : '')}>
                    {!isMine && (m.avatar
                      ? <img src={m.avatar} className="avatar" style={{ width: 'clamp(24px,4vw,30px)', height: 'clamp(24px,4vw,30px)', alignSelf: 'flex-end' }} alt="" />
                      : avatarPh(m.username, 28)
                    )}
                    <div className={"msg-bubble" + (isMine ? ' mine' : '')}>
                      <p>{m.content}</p>
                      <span style={{ fontSize: 'var(--fs-xs)', opacity: .6, marginTop: 3, display: 'block' }}>{format(m.created_at)}</span>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            <form onSubmit={sendMsg} className="msg-form">
              <input className="input" placeholder="Message…" value={msg}
                onChange={e => setMsg(e.target.value)} style={{ flex: 1, borderRadius: 24, padding: 'clamp(9px,1.5vw,12px) clamp(14px,2.5vw,18px)' }} />
              <button type="submit" className="btn btn-primary btn-icon" disabled={!msg.trim() || sending}
                style={{ borderRadius: '50%', width: 'clamp(36px,6vw,44px)', height: 'clamp(36px,6vw,44px)', padding: 0 }}>
                <FiSend size={16} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
